import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Spinner, Row, Col, Nav, Tab, Modal } from 'react-bootstrap';
import { FiArrowLeft, FiUpload, FiDownload } from 'react-icons/fi';
import {
    importFlowerPoliciesCancelled,
    importLacdesgravesPoliciesCancelled,
    importOlelaPoliciesCancelled,
    importOdalysPoliciesCancelled,
    importAloaPoliciesCancelled,
    importTripsPoliciesCancelledGeneric,
} from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import { useTranslation } from '../../hooks/useTranslation';
import TripCancellationImportTab from '../../components/Admin/TripCancellationImportTab';
import {
    getTripCancellationFieldError,
    isTripCancellationResponse,
    parseTripCancellationApiError,
    validateTripCancellationFile,
} from '../../utils/tripCancellationResult';
import { downloadCancelledPoliciesStandardTemplate } from '../../utils/standardImportTemplate';
import 'bootstrap/dist/css/bootstrap.css';
import './AdminTripsPolicies.css';

const PAGE_PREFIX = 'pages.adminTripsPoliciesCancelledImport';

const IMPORT_MODES = {
    STANDARD: 'standard',
    SPECIFIC: 'specific',
};

const STRUCTURED_PARTNERS = ['standard', 'flower', 'aloa', 'odalys'];

const SPECIFIC_PARTNER_OPTIONS = ['flower', 'odalys', 'aloa', 'lacdesgraves', 'olela'];

const STRUCTURED_IMPORT_FNS = {
    standard: importTripsPoliciesCancelledGeneric,
    flower: importFlowerPoliciesCancelled,
    aloa: importAloaPoliciesCancelled,
    odalys: importOdalysPoliciesCancelled,
};

const TEMPLATE_URLS = {
    flower: 'https://eu-west-3.console.aws.amazon.com/s3/object/admin-template-dashboard?region=eu-west-3&prefix=flower/flower_template_cancelled.xlsx',
    olela: 'https://eu-west-3.console.aws.amazon.com/s3/object/admin-template-dashboard?region=eu-west-3&prefix=ol%C3%A9la/olela_template_cancelled.xlsx',
    odalys: 'https://eu-west-3.console.aws.amazon.com/s3/object/admin-template-dashboard?region=eu-west-3&prefix=odalys/odalys_template_cancelled.xlsx',
    lacdesgraves: 'https://eu-west-3.console.aws.amazon.com/s3/object/admin-template-dashboard?region=eu-west-3&prefix=lacdesgraves/lac_des_graves_template_trips_cancelled.xlsx',
};

const FILE_ACCEPT_BY_PARTNER = {
    standard: '.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    flower: '.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    aloa: '.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    odalys: '.xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv',
};

const AdminTripsPoliciesCancelledImport = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [importMode, setImportMode] = useState(IMPORT_MODES.STANDARD);
    const [specificPartner, setSpecificPartner] = useState('flower');
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [structuredResults, setStructuredResults] = useState({
        standard: null,
        flower: null,
        aloa: null,
        odalys: null,
    });
    const [structuredFileErrors, setStructuredFileErrors] = useState({
        standard: null,
        flower: null,
        aloa: null,
        odalys: null,
    });
    const [confirmTarget, setConfirmTarget] = useState(null);
    const [importSuccess, setImportSuccess] = useState(() => {
        const savedMessage = localStorage.getItem('importCancelledPoliciesMessage');
        return savedMessage || null;
    });
    const [templateDownloading, setTemplateDownloading] = useState(false);

    const getStructuredPrefix = (key) => `${PAGE_PREFIX}.${key}`;

    const getActiveImportKey = () => (
        importMode === IMPORT_MODES.STANDARD ? 'standard' : specificPartner
    );

    const isStructuredImportKey = (key) => STRUCTURED_PARTNERS.includes(key);

    const resetImportState = () => {
        setImportFile(null);
        setImportError(null);
        setImportSuccess(null);
        setStructuredResults({ standard: null, flower: null, aloa: null, odalys: null });
        setStructuredFileErrors({ standard: null, flower: null, aloa: null, odalys: null });
        setConfirmTarget(null);
        localStorage.removeItem('importCancelledPoliciesMessage');
    };

    const handleModeChange = (mode) => {
        if (!mode) return;
        setImportMode(mode);
        setImportFile(null);
        setImportError(null);
        setStructuredFileErrors({ standard: null, flower: null, aloa: null, odalys: null });
    };

    const handleSpecificPartnerChange = (partner) => {
        setSpecificPartner(partner);
        setImportFile(null);
        setImportError(null);
        setStructuredFileErrors((prev) => ({ ...prev, [partner]: null }));
    };

    const applyStructuredResult = (key, result) => {
        setStructuredResults((prev) => ({ ...prev, [key]: result }));
        setImportError(null);
        const fieldError = getTripCancellationFieldError(result);
        if (fieldError?.field === 'file') {
            setStructuredFileErrors((prev) => ({ ...prev, [key]: fieldError.reason }));
        } else {
            setStructuredFileErrors((prev) => ({ ...prev, [key]: null }));
        }
    };

    const handleStructuredFileChange = (key, file) => {
        if (!file) {
            setImportFile(null);
            setStructuredFileErrors((prev) => ({ ...prev, [key]: null }));
            return;
        }
        const validation = validateTripCancellationFile(file, key);
        if (!validation.valid) {
            setImportFile(null);
            setStructuredFileErrors((prev) => ({
                ...prev,
                [key]: t(`${getStructuredPrefix(key)}.validation.${validation.reason}`),
            }));
            return;
        }
        setImportFile(file);
        setStructuredFileErrors((prev) => ({ ...prev, [key]: null }));
        setImportError(null);
    };

    const handleStructuredSubmit = (key) => (e) => {
        e.preventDefault();
        if (!importFile) {
            setStructuredFileErrors((prev) => ({
                ...prev,
                [key]: t(`${getStructuredPrefix(key)}.validation.no_file`),
            }));
            return;
        }
        setConfirmTarget(key);
    };

    const handleStructuredConfirmImport = async () => {
        const key = confirmTarget;
        if (!key || !importFile || !STRUCTURED_IMPORT_FNS[key]) return;
        setConfirmTarget(null);
        setImportLoading(true);
        setImportError(null);
        setStructuredResults((prev) => ({ ...prev, [key]: null }));
        setStructuredFileErrors((prev) => ({ ...prev, [key]: null }));

        try {
            const result = await STRUCTURED_IMPORT_FNS[key](importFile);
            applyStructuredResult(key, result);
        } catch (error) {
            const structured = parseTripCancellationApiError(error);
            if (structured) {
                applyStructuredResult(key, structured);
            } else {
                setImportError(handleApiError(error, t(`${getStructuredPrefix(key)}.error`)));
            }
        } finally {
            setImportLoading(false);
        }
    };

    const handleLegacyImport = async () => {
        if (!importFile) {
            setImportError('Veuillez sélectionner un fichier Excel');
            return;
        }

        setImportLoading(true);
        setImportError(null);
        setImportSuccess(null);

        try {
            let response;
            let partnerName;

            if (specificPartner === 'olela') {
                response = await importOlelaPoliciesCancelled(importFile);
                partnerName = 'Olela';
            } else if (specificPartner === 'lacdesgraves') {
                response = await importLacdesgravesPoliciesCancelled(importFile);
                partnerName = 'Lac des Graves';
            } else {
                throw new Error('Type de partenaire non supporté pour l\'annulation');
            }

            let successMessage = `Import des polices annulées ${partnerName} terminé.\n\n`;

            if (response?.success_count !== undefined) {
                successMessage += `✅ Polices mises à jour avec succès : ${response.success_count}\n`;
            }
            if (response?.error_count !== undefined) {
                successMessage += `❌ Erreurs : ${response.error_count}\n`;
            }
            if (response?.updated_policies && Array.isArray(response.updated_policies) && response.updated_policies.length > 0) {
                successMessage += `\n📋 Polices mises à jour : ${response.updated_policies.join(', ')}\n`;
            }
            if (response?.errors && Array.isArray(response.errors) && response.errors.length > 0) {
                successMessage += `\n⚠️ Détails des erreurs :\n`;
                response.errors.forEach((error, index) => {
                    const reservationNumber = error.reservation_number || error.reservationNumber || 'N/A';
                    const errorMsg = error.error || error.message || 'Erreur inconnue';
                    successMessage += `${index + 1}. Réservation ${reservationNumber}: ${errorMsg}\n`;
                });
            }

            if (response?.message) {
                successMessage += `\n${response.message}`;
            }

            setImportSuccess(successMessage);
            localStorage.setItem('importCancelledPoliciesMessage', successMessage);
        } catch (err) {
            setImportError(handleApiError(err, 'Erreur lors de l\'import du fichier Excel'));
        } finally {
            setImportLoading(false);
        }
    };

    const handleReset = () => {
        resetImportState();
        setImportMode(IMPORT_MODES.STANDARD);
        setSpecificPartner('flower');
    };

    const handleClearMessage = () => {
        setImportSuccess(null);
        localStorage.removeItem('importCancelledPoliciesMessage');
    };

    const handleDownloadTemplate = async () => {
        const key = getActiveImportKey();
        if (key === 'standard') {
            if (templateDownloading) return;
            setTemplateDownloading(true);
            setImportError(null);
            try {
                await downloadCancelledPoliciesStandardTemplate();
            } catch (err) {
                setImportError(handleApiError(
                    err,
                    t(`${PAGE_PREFIX}.standard.templateDownloadError`),
                ));
            } finally {
                setTemplateDownloading(false);
            }
            return;
        }
        const templateUrl = TEMPLATE_URLS[key];
        if (!templateUrl) {
            alert(t(`${PAGE_PREFIX}.specificPartners.aloa`) + ' : template indisponible.');
            return;
        }
        window.open(templateUrl, '_blank', 'noopener,noreferrer');
    };

    const renderStandardColumnHelp = () => (
        <>
            <p className="text-muted small mb-2">{t(`${getStructuredPrefix('standard')}.endpointHint`)}</p>
            <p className="small mb-2">{t(`${getStructuredPrefix('standard')}.description`)}</p>
            <strong>{t(`${getStructuredPrefix('standard')}.columnsTitle`)}</strong>
            <ul className="mb-0 mt-2 small">
                <li><code>first_name</code></li>
                <li><code>last_name</code></li>
                <li><code>email</code></li>
                <li><code>reservation_number</code></li>
            </ul>
        </>
    );

    const renderFlowerColumnHelp = () => (
        <>
            <strong>{t(`${getStructuredPrefix('flower')}.columnsTitle`)}</strong>
            <ul className="mb-0 mt-2 small">
                <li><code>first_name</code></li>
                <li><code>last_name</code></li>
                <li><code>email</code></li>
                <li><code>reservation_number</code></li>
            </ul>
        </>
    );

    const renderOdalysColumnHelp = () => (
        <>
            <strong>{t(`${getStructuredPrefix('odalys')}.columnsTitle`)}</strong>
            <p className="mb-2 mt-2 small text-muted">
                {t(`${getStructuredPrefix('odalys')}.formatIresa`)}
                {' · '}
                {t(`${getStructuredPrefix('odalys')}.formatStandardEn`)}
            </p>
            <ul className="mb-0 small">
                <li><code>reservation_number</code></li>
                <li><code>first_name</code></li>
                <li><code>last_name</code></li>
                <li><code>email</code></li>
            </ul>
            <p className="mb-0 mt-2 small text-muted">
                {t(`${getStructuredPrefix('odalys')}.formatAutoDetect`)}
            </p>
        </>
    );

    const renderAloaColumnHelp = () => (
        <>
            <strong>{t(`${getStructuredPrefix('aloa')}.columnsTitle`)}</strong>
            <ul className="mb-0 mt-2 small">
                <li><code>Nom</code></li>
                <li><code>Prénom</code></li>
                <li><code>N° séjour</code></li>
                <li><code>eMail 1</code></li>
                <li><code>Date de création</code></li>
                <li><code>Arrivée le</code></li>
                <li><code>Départ le</code></li>
                <li><code>Rubriques du séjour</code></li>
                <li><code>Nom Camping</code></li>
                <li><code>Etat du séjour</code> ({t(`${getStructuredPrefix('aloa')}.tripStateHint`)})</li>
            </ul>
        </>
    );

    const renderLegacyImportForm = (partnerLabel) => (
        <Form>
            <Row>
                <Col md={12}>
                    <Form.Group className="mb-4">
                        <Form.Label className="fw-bold" style={{ fontSize: '0.95rem', color: '#495057' }}>
                            Fichier Excel <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    setImportFile(file);
                                    setImportError(null);
                                }
                            }}
                            disabled={importLoading}
                        />
                        <Form.Text className="text-muted">
                            Sélectionnez un fichier Excel (.xlsx, .xls) contenant les séjours annulés {partnerLabel}
                        </Form.Text>
                        {importFile && (
                            <div className="mt-2 small text-success">
                                ✓ Fichier sélectionné : {importFile.name}
                            </div>
                        )}
                    </Form.Group>
                </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2 mt-4">
                <Button variant="outline-secondary" onClick={handleReset} disabled={importLoading}>
                    {t('common.reset')}
                </Button>
                <Button variant="primary" onClick={handleLegacyImport} disabled={importLoading || !importFile}>
                    {importLoading ? (
                        <>
                            <Spinner size="sm" className="me-2" animation="border" />
                            Import en cours...
                        </>
                    ) : (
                        <>
                            <FiUpload className="me-2" />
                            Importer
                        </>
                    )}
                </Button>
            </div>
        </Form>
    );

    const renderStructuredImportTab = (partner) => (
        <TripCancellationImportTab
            partner={partner}
            importFile={importFile}
            fileError={structuredFileErrors[partner]}
            importLoading={importLoading}
            result={structuredResults[partner]}
            fileAccept={FILE_ACCEPT_BY_PARTNER[partner]}
            businessAlert={partner === 'odalys' ? (
                <Alert variant="info" className="mb-4">
                    {t(`${getStructuredPrefix('odalys')}.businessNote`)}
                </Alert>
            ) : partner === 'aloa' ? (
                <Alert variant="warning" className="mb-4">
                    {t(`${getStructuredPrefix('aloa')}.businessNote`)}
                </Alert>
            ) : null}
            columnHelp={(
                partner === 'standard' ? renderStandardColumnHelp()
                    : partner === 'flower' ? renderFlowerColumnHelp()
                        : partner === 'odalys' ? renderOdalysColumnHelp()
                            : renderAloaColumnHelp()
            )}
            onFileChange={(file) => handleStructuredFileChange(partner, file)}
            onSubmit={handleStructuredSubmit(partner)}
            onReset={handleReset}
            onDismissResult={() => setStructuredResults((prev) => ({ ...prev, [partner]: null }))}
        />
    );

    const renderSpecificPartnerContent = () => {
        if (isStructuredImportKey(specificPartner)) {
            return renderStructuredImportTab(specificPartner);
        }
        const partnerLabel = t(`${PAGE_PREFIX}.specificPartners.${specificPartner}`);
        return renderLegacyImportForm(partnerLabel);
    };

    const activeImportKey = getActiveImportKey();
    const showStructuredError = isStructuredImportKey(activeImportKey)
        && importError
        && !isTripCancellationResponse(structuredResults[activeImportKey]);
    const showLegacyError = !isStructuredImportKey(activeImportKey) && importError;
    const showLegacySuccess = !isStructuredImportKey(activeImportKey) && importSuccess;

    return (
        <div className="dashboard-page admin-trips-policies-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiUpload className="me-2" />
                                Importer des polices annulées
                            </h2>
                            <p className="text-muted mb-0">
                                Importez un fichier Excel de séjours annulés pour mettre à jour les polices et rejeter les sinistres associés
                            </p>
                        </div>
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={handleDownloadTemplate}
                                disabled={templateDownloading || importLoading}
                                className="d-flex align-items-center"
                            >
                                {templateDownloading ? (
                                    <Spinner size="sm" className="me-2" animation="border" aria-hidden />
                                ) : (
                                    <FiDownload className="me-2" aria-hidden />
                                )}
                                Télécharger le template
                            </Button>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                    </div>

                    {importLoading && (
                        <Alert variant="info" className="mb-4">
                            <Spinner size="sm" className="me-2" animation="border" />
                            <strong>Import en cours...</strong> Cela peut prendre plusieurs minutes selon la taille du fichier. Veuillez patienter et ne pas fermer cette page.
                        </Alert>
                    )}

                    <Card className="import-form-card" style={{ border: '1px solid #e9ecef', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                        <Card.Body style={{ padding: '2rem' }}>
                            <Tab.Container activeKey={importMode} onSelect={handleModeChange}>
                                <Nav variant="tabs" className="mb-4">
                                    <Nav.Item>
                                        <Nav.Link eventKey={IMPORT_MODES.STANDARD}>
                                            {t(`${PAGE_PREFIX}.pageTabs.standard`)}
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link eventKey={IMPORT_MODES.SPECIFIC}>
                                            {t(`${PAGE_PREFIX}.pageTabs.specific`)}
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>

                                <Tab.Content>
                                    <Tab.Pane eventKey={IMPORT_MODES.STANDARD}>
                                        {renderStructuredImportTab('standard')}
                                    </Tab.Pane>

                                    <Tab.Pane eventKey={IMPORT_MODES.SPECIFIC}>
                                        <Form.Group className="mb-4" style={{ maxWidth: '360px' }}>
                                            <Form.Label className="fw-bold">
                                                {t(`${PAGE_PREFIX}.specificPartnerLabel`)}
                                            </Form.Label>
                                            <Form.Select
                                                value={specificPartner}
                                                onChange={(e) => handleSpecificPartnerChange(e.target.value)}
                                                disabled={importLoading}
                                            >
                                                {SPECIFIC_PARTNER_OPTIONS.map((partner) => (
                                                    <option key={partner} value={partner}>
                                                        {t(`${PAGE_PREFIX}.specificPartners.${partner}`)}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                        {renderSpecificPartnerContent()}
                                    </Tab.Pane>
                                </Tab.Content>
                            </Tab.Container>
                        </Card.Body>
                    </Card>

                    {showLegacyError && (
                        <Alert variant="danger" className="mb-4 mt-4" dismissible onClose={() => setImportError(null)}>
                            <strong>Erreur :</strong> {importError}
                        </Alert>
                    )}

                    {showStructuredError && (
                        <Alert variant="danger" className="mb-4 mt-4" dismissible onClose={() => setImportError(null)}>
                            <strong>{t('common.error')} :</strong> {importError}
                        </Alert>
                    )}

                    {showLegacySuccess && (
                        <Alert
                            variant={importSuccess.includes('❌') || importSuccess.includes('Erreurs :') ? 'warning' : 'success'}
                            className="mb-4 mt-4"
                            dismissible
                            onClose={handleClearMessage}
                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                            <strong>Résultat de l&apos;import :</strong>
                            <pre style={{
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                marginTop: '10px',
                                marginBottom: 0,
                                fontSize: '0.9rem',
                                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                padding: '10px',
                                borderRadius: '4px',
                                fontFamily: 'inherit',
                                maxHeight: '400px',
                                overflowY: 'auto',
                            }}
                            >
                                {importSuccess}
                            </pre>
                        </Alert>
                    )}

                    <Modal show={!!confirmTarget} onHide={() => setConfirmTarget(null)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>
                                {confirmTarget ? t(`${getStructuredPrefix(confirmTarget)}.confirmTitle`) : ''}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {confirmTarget && (
                                <>
                                    <p className="mb-2">{t(`${getStructuredPrefix(confirmTarget)}.confirmMessage`)}</p>
                                    <ul className="mb-2">
                                        <li>
                                            {t(`${getStructuredPrefix(confirmTarget)}.filterFilename`)} :{' '}
                                            <strong>{importFile?.name || '—'}</strong>
                                        </li>
                                    </ul>
                                    <p className="text-muted small mb-0">
                                        {t(`${getStructuredPrefix(confirmTarget)}.confirmWarning`)}
                                    </p>
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setConfirmTarget(null)} disabled={importLoading}>
                                {t('common.cancel')}
                            </Button>
                            <Button variant="primary" onClick={handleStructuredConfirmImport} disabled={importLoading}>
                                {confirmTarget ? t(`${getStructuredPrefix(confirmTarget)}.confirmSend`) : t('common.confirm')}
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminTripsPoliciesCancelledImport;
